import { last, pick, isEmpty } from 'lodash'

import PreOrdersItemViewModel from '../viewmodels/PreOrdersItemViewModel'

import { stringFloatToCents } from '../utils/formatters'
import { formatOrderDetails, makeClientPromotionParams } from '../utils/orderTotalUtils'

import PaymentTypes from '../utils/paymentTypes'

import { backendOrderModelKeys } from './order/attributes'

// TODO - split payments?
const getPaymentData = (order) => {
  const paymentInfo = order?.paymentInfo ?? []

  return last(paymentInfo) || {}
}

const formatPaymentInfo = (order, paymentData) => {
  if (PaymentTypes.isCardRequired(order)) return 'Card Required'

  let { paymentType, shortDescription } = paymentData

  if (PaymentTypes.isMobileWalletPayment(paymentType)) shortDescription = shortDescription ?? 'Mobile Pay'

  if (PaymentTypes.isCash(paymentType)) return 'Cash'
  // NOTE (westinschepper): This is temporary
  if (PaymentTypes.isDirectBill(paymentType)) return 'Payment Pending'
  if (PaymentTypes.isHouseAccount(paymentType)) return 'House Account'
  if (PaymentTypes.isFnbCredit(paymentType)) return 'F&B Credit'

  if (shortDescription) return shortDescription

  return paymentType
}

const isRemotePayment = (paymentData) => {
  const { paymentType } = paymentData

  return PaymentTypes.isRemotePayment(paymentType)
}

const separateLineItemsAndFees = (allLineItems = []) => {
  let lineItems = []
  let serviceChargeLineItem, taxLineItem = undefined

  allLineItems.forEach((li) => {
    if (!li.itemSpecialType) {
      lineItems.push(li)
    } else if (li.itemSpecialType === 'service_charge' || li.itemSpecialType === 'taxable_service_charge') {
      serviceChargeLineItem = li
    } else if (li.itemSpecialType === 'tax') {
      taxLineItem = li
    }
  })

  return { lineItems, serviceChargeLineItem, taxLineItem }
}

const parseOrderPricing = (order, lineItems, serviceChargeLineItem, taxLineItem, tipAmountInCents) => {
  const discountString = order?.discountAmountInCents ?? '0' // don't be fooled, it's a string
  const totalString = order?.total ?? '0.00'
  const resolvedLineItems = lineItems
  const scFraction = serviceChargeLineItem?.fractionalQuantity ?? 1

  if (serviceChargeLineItem !== undefined) {
    resolvedLineItems.push(serviceChargeLineItem)
  }

  return formatOrderDetails({
    discountAmountInCents: parseInt(discountString),
    lineItems: resolvedLineItems,
    promotions: order.promotions,
    serviceChargeInCents: scFraction * stringFloatToCents(serviceChargeLineItem?.price ?? '0.0'),
    taxAmountInCents: stringFloatToCents(taxLineItem?.price ?? '0.0'),
    tipAmountInCents,
    totalAmountInCents: stringFloatToCents(totalString),
    updatedAt: order.updatedAt,
  })
}

export const orderFromBackend = (backendOrder = {}) => {
  let order = pick(backendOrder, backendOrderModelKeys)

  const { lineItems, serviceChargeLineItem, taxLineItem } = separateLineItemsAndFees(order?.lineItems ?? [])
  const paymentData = getPaymentData(order)
  const safeOrderTotal = order?.total ?? ''
  const tipAmountInCents = stringFloatToCents(order?.tip ?? '0.00')

  // The full JSON of the submission result does not need to be stored; only the order number.
  order.orderNumber = backendOrder?.orderSubmissionResult?.orderNumber ?? 'unknown'

  // Only a subset of the promotion is needed for follow-on totaling.
  order.promotions = makeClientPromotionParams(order.promotions)

  order.amountInCents = parseInt(safeOrderTotal.replace('.', ''))
  order.isOnline = true
  order.isRemotePayment = isRemotePayment(paymentData)
  order.isSyncing = false
  order.modifiedAt = order.updatedAt
  order.frozen = !!order.frozen
  order.paymentDisplay = formatPaymentInfo(order, paymentData)
  order.submittedAt = order.createdAt
  order.tipAmountInCents = tipAmountInCents
  order.syncedAt = order.updatedAt
  order.customerPaymentId = paymentData.customerPaymentId

  if (!isEmpty(lineItems)) {
    order.itemModels = lineItems.map(lineItem => PreOrdersItemViewModel(lineItem))
    order.orderTotalFromOrder = parseOrderPricing(order, lineItems, serviceChargeLineItem, taxLineItem, tipAmountInCents)
  }

  return order
}

export default orderFromBackend
